import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom, tap, filter } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

import * as BrandActions from '../actions/brand.actions';
import * as AuthActions from '../actions/auth.actions';
import * as fromApp from '../app.state';
import { BrandService } from '../../services/brand.service';
import { Brand } from '../../../shared/interfaces/brand.type';
import { getStoredActiveBrandId, setStoredActiveBrandId } from '../../helpers/brand-storage';

@Injectable()
export class BrandEffects {
    constructor(
        private actions$: Actions,
        private store: Store<fromApp.AppState>,
        private brandService: BrandService,
    ) {}

    /**
     * Fetch the list of brands the current user is allowed to work on.
     * Triggered on login success and on app boot when a session is restored.
     */
    loadBrands$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BrandActions.loadBrands),
            switchMap(() => this.brandService.getMyBrands().pipe(
                map((res: any) => {
                    // Postgres bigint comes back over the wire as a string;
                    // coerce ids to numbers so every downstream compare (selectors,
                    // reconciliation, X-Brand-Id header) works with one type.
                    const brands: Brand[] = (Array.isArray(res?.data) ? res.data : [])
                        .map((b: any) => ({ ...b, id: Number(b.id) }));
                    return BrandActions.loadBrandsSuccess({ brands });
                }),
                catchError((error: HttpErrorResponse) => of(BrandActions.loadBrandsFailure({ error }))),
            )),
        ),
    );

    /**
     * Reconcile the active brand after a load.
     * - Stored brand still in the list -> keep it
     * - Otherwise -> first accessible brand
     * - Empty list -> null
     *
     * This is also the path that handles "permissions changed → previously
     * remembered brand is no longer accessible" — same reconciliation rule.
     */
    reconcileActiveBrand$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BrandActions.loadBrandsSuccess),
            withLatestFrom(this.store.select(fromApp.selectCurrentUser)),
            map(([{ brands }, user]) => {
                const userId = user?.id ?? null;
                if (!brands.length) {
                    return BrandActions.setActiveBrand({ brandId: null });
                }
                const storedId = getStoredActiveBrandId(userId);
                const stillAccessible = storedId != null && brands.some((b) => Number(b.id) === Number(storedId));
                const brandId = stillAccessible ? Number(storedId) : Number(brands[0].id);
                return BrandActions.setActiveBrand({ brandId });
            }),
        ),
    );

    /** Persist the active brand per-user whenever it changes. */
    persistActiveBrand$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BrandActions.setActiveBrand),
            withLatestFrom(this.store.select(fromApp.selectCurrentUser)),
            tap(([{ brandId }, user]) => {
                setStoredActiveBrandId(user?.id ?? null, brandId);
            }),
        ),
        { dispatch: false },
    );

    /** Load brands once we have an authenticated session. */
    loadOnLogin$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.loginSuccess, AuthActions.refreshTokenSuccess),
            map(() => BrandActions.loadBrands()),
        ),
    );

    /** Wipe in-memory brand state on logout/unauthorized. The per-user
     * localStorage entry is intentionally left intact so re-login restores
     * the user's previous brand. */
    clearOnLogout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.logout, AuthActions.unAuthorized),
            map(() => BrandActions.clearBrandState()),
        ),
    );

    /**
     * If a user object reloads (e.g. after `updateUser`) and brings a fresh
     * permissions set, re-fetch the accessible brand list so a permissions
     * change is picked up without a full re-login. Guarded by isAuthenticated
     * so it doesn't fire on the post-logout clearing.
     */
    reloadOnUserUpdate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.updateUser),
            withLatestFrom(this.store.select(fromApp.selectAuthState)),
            filter(([_, auth]) => !!auth?.isAuthenticated),
            map(() => BrandActions.loadBrands()),
        ),
    );
}
